/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography as MuiTypography,
} from "@mui/material";
import React, { ReactNode } from "react";

/**
 * Maps each semantic role in the D365 long description to a branded React element.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║  THIS IS THE SINGLE SOURCE OF PRESENTATION TRUTH FOR PRODUCT             ║
 * ║  DESCRIPTIONS.                                                           ║
 * ║                                                                          ║
 * ║  To change the visual treatment of any element, edit the corresponding   ║
 * ║  function below. Changes here automatically apply to every place the     ║
 * ║  LongDescription component is used (Product Details + Campaign Modal).   ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * HTML structure → role mapping (defined by the HTML Usage Expectations standard):
 *   <p><strong>…</strong></p>   → heading     (sole child is <strong>)
 *   <p>…</p>                    → paragraph
 *   <ul>…</ul>                  → list
 *   <li>…</li>                  → listItem
 *   <table>…</table>            → table       (wrapped in a horizontal-scroll container)
 *   <thead>…</thead>            → tableHead
 *   <tbody>…</tbody>            → tableBody
 *   <tr>…</tr>                  → row
 *   <th>…</th>                  → headerCell
 *   <td>…</td>                  → dataCell
 */
export type DescriptionStyleMap = {
  heading: (children: ReactNode) => React.ReactElement;
  paragraph: (children: ReactNode) => React.ReactElement;
  list: (children: ReactNode) => React.ReactElement;
  listItem: (children: ReactNode) => React.ReactElement;
  table: (children: ReactNode) => React.ReactElement;
  tableHead: (children: ReactNode) => React.ReactElement;
  tableBody: (children: ReactNode) => React.ReactElement;
  row: (children: ReactNode) => React.ReactElement;
  headerCell: (children: ReactNode) => React.ReactElement;
  dataCell: (children: ReactNode) => React.ReactElement;
};

// NOTE: All styles below are placeholders. Update once UX approves the mapping.
export const descriptionStyleMap: DescriptionStyleMap = {
  // <p><strong>Section heading</strong></p>
  heading: (children) => (
    <MuiTypography variant="body1" component="h3" fontWeight="bold" sx={{ mt: 2, mb: 0.5 }}>
      {children}
    </MuiTypography>
  ),

  // <p>Body paragraph</p>
  paragraph: (children) => (
    <MuiTypography variant="body2" component="p" sx={{ mb: 1 }}>
      {children}
    </MuiTypography>
  ),

  // <ul>
  list: (children) => (
    <Box component="ul" sx={{ pl: 3, my: 1, listStyleType: "disc" }}>
      {children}
    </Box>
  ),

  // <li>
  listItem: (children) => (
    <Box component="li" sx={{ mb: 0.5 }}>
      <MuiTypography variant="body2" component="span">
        {children}
      </MuiTypography>
    </Box>
  ),

  // <table> — wrapped in a horizontal-scroll container so it works on narrow viewports.
  // To change the table layout behaviour (e.g. stacked on mobile), update the Box sx here.
  table: (children) => (
    <Box sx={{ overflowX: "auto", my: 2 }}>
      <Table size="small">{children}</Table>
    </Box>
  ),

  // <thead>
  tableHead: (children) => <TableHead>{children}</TableHead>,

  // <tbody>
  tableBody: (children) => <TableBody>{children}</TableBody>,

  // <tr>
  row: (children) => <TableRow>{children}</TableRow>,

  // <th>
  headerCell: (children) => <TableCell sx={{ fontWeight: "bold" }}>{children}</TableCell>,

  // <td>
  dataCell: (children) => <TableCell>{children}</TableCell>,
};
